import * as CONSTANTS from './canvasSymTypes';

/**
 * Mapping from canvas key IDs (runtime values from CONSTANTS) to
 * kid-friendly spoken descriptions for Text-to-Speech.
 *
 */
export const MATH_KEY_SPOKEN_NAMES: { [key: string]: string } = {
  // Basic numbers
  [CONSTANTS.KEY_ID_0]: 'zero',
  [CONSTANTS.KEY_ID_1]: 'one',
  [CONSTANTS.KEY_ID_2]: 'two',
  [CONSTANTS.KEY_ID_3]: 'three',
  [CONSTANTS.KEY_ID_4]: 'four',
  [CONSTANTS.KEY_ID_5]: 'five',
  [CONSTANTS.KEY_ID_6]: 'six',
  [CONSTANTS.KEY_ID_7]: 'seven',
  [CONSTANTS.KEY_ID_8]: 'eight',
  [CONSTANTS.KEY_ID_9]: 'nine',

  // Text mode
  [CONSTANTS.KEY_ID_ABC]: 'start text mode',

  // Brackets and parentheses
  [CONSTANTS.KEY_ID_PAREN_LEFT]: 'open parenthesis',
  [CONSTANTS.KEY_ID_PAREN_RIGHT]: 'close parenthesis',
  [CONSTANTS.KEY_ID_SQ_BRACKET_LEFT]: 'open bracket',
  [CONSTANTS.KEY_ID_SQ_BRACKET_RIGHT]: 'close bracket',
  [CONSTANTS.KEY_ID_CURLY_LEFT]: 'open curly brace',
  [CONSTANTS.KEY_ID_CURLY_RIGHT]: 'close curly brace',

  // Common operations
  [CONSTANTS.KEY_ID_PLUS]: 'plus',
  [CONSTANTS.KEY_ID_MINUS]: 'minus',
  [CONSTANTS.KEY_ID_NEGATIVE_SIGN]: 'negative',
  [CONSTANTS.KEY_ID_MULTIPLY_1]: 'times',
  [CONSTANTS.KEY_ID_MULTIPLY_2]: 'times',
  [CONSTANTS.KEY_ID_MULTIPLY_3]: 'times',
  [CONSTANTS.KEY_ID_DIVIDE_1]: 'divided by',
  [CONSTANTS.KEY_ID_DIVIDE_2]: 'divided by',
  [CONSTANTS.KEY_ID_DECIMAL]: 'point',
  [CONSTANTS.KEY_ID_LESS_THAN]: 'less than',
  [CONSTANTS.KEY_ID_LESS_THAN_EQ]: 'less than or equal to',
  [CONSTANTS.KEY_ID_GREATER_THAN]: 'greater than',
  [CONSTANTS.KEY_ID_GREATER_THAN_EQ]: 'greater than or equal to',
  [CONSTANTS.KEY_ID_EQUAL]: 'equals',
  [CONSTANTS.KEY_ID_NOT_EQUAL]: 'not equal to',
  [CONSTANTS.KEY_ID_APPROX_EQUAL]: 'approximately equal to',

  // Fractions, roots, division, exponents
  [CONSTANTS.KEY_ID_FRACTION]: 'fraction',
  [CONSTANTS.KEY_ID_FRACTION_GRID]: 'fraction',
  [CONSTANTS.KEY_ID_FRACTION_GRID_SIGN]: 'fraction',
  [CONSTANTS.KEY_ID_DIVISION]: 'long division',
  [CONSTANTS.KEY_ID_SQRT_SIGN]: 'square root',
  [CONSTANTS.KEY_ID_SQRT]: 'square root',
  [CONSTANTS.KEY_ID_ROOT]: 'n-th root',
  [CONSTANTS.KEY_ID_EXPONENT]: 'to the power of',
  [CONSTANTS.KEY_ID_EXPONENT_1]: 'subscript',
  [CONSTANTS.KEY_ID_EXPONENT_2]: 'left superscript',
  [CONSTANTS.KEY_ID_E_EXPONENT]: 'e to the power of',

  // Trigonometric and logarithmic functions
  [CONSTANTS.KEY_ID_SIN]: 'sine',
  [CONSTANTS.KEY_ID_SIN_SQR]: 'sine squared',
  [CONSTANTS.KEY_ID_INVERSE_SIN]: 'inverse sine',
  [CONSTANTS.KEY_ID_COS]: 'cosine',
  [CONSTANTS.KEY_ID_COS_SQR]: 'cosine squared',
  [CONSTANTS.KEY_ID_INVERSE_COS]: 'inverse cosine',
  [CONSTANTS.KEY_ID_TAN]: 'tangent',
  [CONSTANTS.KEY_ID_TAN_SQR]: 'tangent squared',
  [CONSTANTS.KEY_ID_INVERSE_TAN]: 'inverse tangent',
  [CONSTANTS.KEY_ID_SEC]: 'secant',
  [CONSTANTS.KEY_ID_SEC_SQR]: 'secant squared',
  [CONSTANTS.KEY_ID_INVERSE_SEC]: 'inverse secant',
  [CONSTANTS.KEY_ID_CSC]: 'cosecant',
  [CONSTANTS.KEY_ID_CSC_SQR]: 'cosecant squared',
  [CONSTANTS.KEY_ID_INVERSE_CSC]: 'inverse cosecant',
  [CONSTANTS.KEY_ID_COT]: 'cotangent',
  [CONSTANTS.KEY_ID_COT_SQR]: 'cotangent squared',
  [CONSTANTS.KEY_ID_INVERSE_COT]: 'inverse cotangent',
  [CONSTANTS.KEY_ID_LOG]: 'log',
  [CONSTANTS.KEY_ID_LOG_A]: 'log base',
  [CONSTANTS.KEY_ID_LN]: 'natural log',

  // Units and currency
  [CONSTANTS.KEY_ID_PERCENT]: 'percent',
  [CONSTANTS.KEY_ID_CENT]: 'cent sign',
  [CONSTANTS.KEY_ID_DEGREE]: 'degree',
  [CONSTANTS.KEY_ID_FT]: 'feet unit',
  [CONSTANTS.KEY_ID_IN]: 'inches',
  [CONSTANTS.KEY_ID_FT_MARK]: 'foot mark',
  [CONSTANTS.KEY_ID_IN_MARK]: 'inches mark',
  [CONSTANTS.KEY_ID_LB]: 'pounds weight',
  [CONSTANTS.KEY_ID_KG]: 'kilograms',
  [CONSTANTS.KEY_ID_CM]: 'centimeters',
  [CONSTANTS.KEY_ID_METER]: 'meters',
  [CONSTANTS.KEY_ID_DOLLAR]: 'dollars',
  [CONSTANTS.KEY_ID_EURO]: 'euros',
  [CONSTANTS.KEY_ID_BR_POUND]: 'British pounds',
  [CONSTANTS.KEY_ID_YEN]: 'yen',
  [CONSTANTS.KEY_ID_RUPEE]: 'rupee',
  [CONSTANTS.KEY_ID_AM]: 'A.M.',
  [CONSTANTS.KEY_ID_PM]: 'P.M.',
  [CONSTANTS.KEY_ID_OUNCE]: 'ounces',
  // Note: KEY_ID_KM currently has runtime value '€' (same as KEY_ID_EURO), so using KEY_ID_EURO above
  // When KEY_ID_KM is fixed to 'km', add: [CONSTANTS.KEY_ID_KM]: 'kilometers',
  [CONSTANTS.KEY_ID_LITER]: 'liters',
  [CONSTANTS.KEY_ID_MONEY_Q]: 'quarter',
  [CONSTANTS.KEY_ID_MONEY_D]: 'dime',
  [CONSTANTS.KEY_ID_MONEY_N]: 'nickel',
  [CONSTANTS.KEY_ID_MONEY_P]: 'penny',

  // Variables and powers
  [CONSTANTS.KEY_ID_X]: 'ex',
  [CONSTANTS.KEY_ID_Y]: 'why',
  [CONSTANTS.KEY_ID_Z]: 'z',
  [CONSTANTS.KEY_ID_W]: 'w',
  [CONSTANTS.KEY_ID_A]: 'letter A',
  [CONSTANTS.KEY_ID_B]: 'b',
  [CONSTANTS.KEY_ID_C]: 'c',
  [CONSTANTS.KEY_ID_N]: 'n',
  [CONSTANTS.KEY_ID_H]: 'h',
  [CONSTANTS.KEY_ID_R]: 'r', // Also covers KEY_ID_REMAINDER (both evaluate to 'r')
  [CONSTANTS.KEY_ID_S]: 's',
  [CONSTANTS.KEY_ID_T]: 't',
  [CONSTANTS.KEY_ID_FX]: 'f of x',
  [CONSTANTS.KEY_ID_P]: 'p',
  [CONSTANTS.KEY_ID_THETA]: 'theta',
  [CONSTANTS.KEY_ID_M]: 'em',
  [CONSTANTS.KEY_ID_MU]: 'mew',
  [CONSTANTS.KEY_ID_Q]: 'q',
  // Additional plain-letter variables typed from keyboard (no dedicated KEY_ID_* constant)
  d: 'd',
  f: 'f',
  g: 'g',
  j: 'j',
  k: 'k',
  l: 'l',
  o: 'o',
  u: 'u',
  v: 'v',
  [CONSTANTS.KEY_ID_X_SQR]: 'x squared',
  [CONSTANTS.KEY_ID_Y_SQR]: 'y squared',
  [CONSTANTS.KEY_ID_Z_SQR]: 'z squared',
  [CONSTANTS.KEY_ID_A_SQR]: 'A squared',
  [CONSTANTS.KEY_ID_B_SQR]: 'b squared',
  [CONSTANTS.KEY_ID_C_SQR]: 'c squared',
  [CONSTANTS.KEY_ID_R_SQR]: 'r squared',
  [CONSTANTS.KEY_ID_T_SQR]: 't squared',
  [CONSTANTS.KEY_ID_N_SQR]: 'n squared',

  // Constants and special math symbols
  [CONSTANTS.KEY_ID_PI]: 'pie',
  // Note: KEY_PI_ID has same runtime value as KEY_ID_PI ('π'), so using KEY_ID_PI above
  [CONSTANTS.KEY_ID_E]: 'e',
  [CONSTANTS.KEY_ID_I]: 'imaginary eye',
  [CONSTANTS.KEY_ID_AVOGADRO]: "Avogadro's number",
  [CONSTANTS.KEY_ID_INFINITY]: 'infinity',
  [CONSTANTS.KEY_ID_ANGLE_VALUE]: 'angle measure',
  [CONSTANTS.KEY_ID_ANGLE]: 'angle',
  [CONSTANTS.KEY_ID_PLUS_MINUS]: 'plus or minus',
  [CONSTANTS.KEY_ID_UNION]: 'union',
  [CONSTANTS.KEY_ID_INTERSECT]: 'intersection',
  [CONSTANTS.KEY_ID_ELEMENT]: 'is an element of',
  [CONSTANTS.KEY_ID_FACTORIAL]: 'factorial',
  [CONSTANTS.KEY_ID_ABSOLUTE]: 'absolute value',
  [CONSTANTS.KEY_ID_COMMA]: 'comma',
  [CONSTANTS.KEY_ID_COLON]: 'colon',

  // Calculus symbols
  [CONSTANTS.KEY_ID_SUMMATION]: 'summation',
  [CONSTANTS.KEY_ID_SUMMATION_1]: 'summation with limits',
  [CONSTANTS.KEY_ID_SUMMATION_2]: 'limit',
  [CONSTANTS.KEY_ID_DELTA]: 'Greek letter Delta',
  [CONSTANTS.KEY_ID_DX]: 'd x',
  [CONSTANTS.KEY_ID_DY]: 'd y',
  [CONSTANTS.KEY_ID_DT]: 'd t',
  [CONSTANTS.KEY_ID_DER_F]: 'f prime',
  [CONSTANTS.KEY_ID_DER_F2]: 'f double prime',
  [CONSTANTS.KEY_ID_DER_1]: 'd over d x',
  [CONSTANTS.KEY_ID_DER_2]: 'd v over d x',
  [CONSTANTS.KEY_ID_DER_3]: 'd over d t',
  [CONSTANTS.KEY_ID_DER_4]: 'second derivative',
  [CONSTANTS.KEY_ID_DER_5]: 'd y over d x',
  [CONSTANTS.KEY_ID_DER_6]: 'd squared y over d x squared',
  [CONSTANTS.KEY_ID_DER_7]: 'd squared y over d t squared',
  [CONSTANTS.KEY_ID_VERTICAL_LINE]: 'evaluated from',
  [CONSTANTS.KEY_ID_INTEGRAL]: 'indefinite integral',
  [CONSTANTS.KEY_ID_INTEGRAL_1]: 'integral',

  // Miscellaneous symbols and controls
  [CONSTANTS.KEY_ID_SPACE]: 'space',
  [CONSTANTS.KEY_ID_TAB]: 'tab',
  [CONSTANTS.KEY_ID_TAB_EQUAL]: 'tab to equals',
  [CONSTANTS.KEY_ID_LINE]: 'answer line',
  [CONSTANTS.KEY_ID_LINE_1]: 'answer line',
  [CONSTANTS.KEY_ID_CHEM_ARROW]: 'right arrow',
  [CONSTANTS.KEY_ID_CHEM_DB_ARROW]: 'equilibrium arrow',
  [CONSTANTS.KEY_ID_LEFT_ARROW]: 'left arrow',
  [CONSTANTS.KEY_ID_CHECKMARK]: 'checkmark',

  // Cross-off and regroup keys
  [CONSTANTS.KEY_ID_CROSSOFF_1]: 'cross off and regroup',
  [CONSTANTS.KEY_ID_CROSSOFF_2]: 'cross off',
  [CONSTANTS.KEY_ID_CROSSOFF_3]: 'regroup box',

  // Canvas control keys we may want to speak in tutorials
  [CONSTANTS.CV_CNTL_ENTER]: 'new line',
  [CONSTANTS.CV_CNTL_NEW_PROBLEM]: 'new problem',
  [CONSTANTS.CV_CNTL_NEW_GRID_PROBLEM]: 'new grid problem',
  [CONSTANTS.CV_CNTL_BACKSPACE]: 'backspace',
  [CONSTANTS.CV_CNTL_ARROWLEFT]: 'Move left',
  [CONSTANTS.CV_CNTL_ARROWRIGHT]: 'Move right',
  [CONSTANTS.CV_CNTL_ARROWUP]: 'Move Up',
  [CONSTANTS.CV_CNTL_ARROWDOWN]: 'Move Down',

  // Geometry keyboard symbols
  [CONSTANTS.KEY_ID_TRIANGLE]: 'triangle',
  [CONSTANTS.KEY_ID_GEO_CIRCLE]: 'circle',
  [CONSTANTS.KEY_ID_CONGRUENT]: 'congruent to',
  [CONSTANTS.KEY_ID_SIMILAR]: 'similar to',
  [CONSTANTS.KEY_ID_NOT_SIMILAR]: 'not similar to',
  [CONSTANTS.KEY_ID_PARALLEL]: 'parallel to',
  [CONSTANTS.KEY_ID_PERPENDICULAR]: 'perpendicular to',
  [CONSTANTS.KEY_ID_PRIME]: 'prime',
  [CONSTANTS.KEY_ID_DOUBLE_PRIME]: 'double prime',
  [CONSTANTS.KEY_ID_MEASURE_ANGLE]: 'measure of angle',
  [CONSTANTS.KEY_ID_THEREFORE]: 'therefore',
  [CONSTANTS.KEY_ID_BECAUSE]: 'because',
  [CONSTANTS.KEY_ID_RIGHT_ANGLE]: 'right angle',

  // Geometry over-text (image) symbols
  [CONSTANTS.KEY_ID_GEO_LINE_SEG]: 'line segment',
  [CONSTANTS.KEY_ID_GEO_RAY]: 'ray',
  [CONSTANTS.KEY_ID_GEO_LINE]: 'line',
  [CONSTANTS.KEY_ID_GEO_ARC]: 'arc',

  // Geometry capital-letter variables
  [CONSTANTS.KEY_ID_CAP_A]: 'capital A',
  [CONSTANTS.KEY_ID_CAP_B]: 'capital B',
  [CONSTANTS.KEY_ID_CAP_C]: 'capital C',
  [CONSTANTS.KEY_ID_CAP_D]: 'capital D',
  [CONSTANTS.KEY_ID_CAP_E]: 'capital E',
  [CONSTANTS.KEY_ID_CAP_F]: 'capital F',
  [CONSTANTS.KEY_ID_CAP_G]: 'capital G',
  [CONSTANTS.KEY_ID_CAP_H]: 'capital H',
  [CONSTANTS.KEY_ID_CAP_I]: 'capital I',
  [CONSTANTS.KEY_ID_CAP_M]: 'capital M',
  [CONSTANTS.KEY_ID_CAP_N]: 'capital N',
  [CONSTANTS.KEY_ID_CAP_O]: 'capital O',
  [CONSTANTS.KEY_ID_CAP_P]: 'capital P',
  [CONSTANTS.KEY_ID_CAP_Q]: 'capital Q',
  [CONSTANTS.KEY_ID_CAP_R]: 'capital R',
  [CONSTANTS.KEY_ID_CAP_X]: 'capital X',
  [CONSTANTS.KEY_ID_CAP_Y]: 'capital Y',
  [CONSTANTS.KEY_ID_CAP_Z]: 'capital Z',

  // Geometry area / volume units (KEY_ID_U stays 'u' via the plain-letter entry above)
  [CONSTANTS.KEY_ID_CM_SQR]: 'square centimeters',
  [CONSTANTS.KEY_ID_M_SQR]: 'square meters',
  [CONSTANTS.KEY_ID_IN_SQR]: 'square inches',
  [CONSTANTS.KEY_ID_FT_SQR]: 'square feet',
  [CONSTANTS.KEY_ID_CM_CUBE]: 'cubic centimeters',
  [CONSTANTS.KEY_ID_M_CUBE]: 'cubic meters',
  [CONSTANTS.KEY_ID_IN_CUBE]: 'cubic inches',
  [CONSTANTS.KEY_ID_FT_CUBE]: 'cubic feet',
  [CONSTANTS.KEY_ID_YD]: 'yards',
  [CONSTANTS.KEY_ID_MI]: 'miles',
  [CONSTANTS.KEY_ID_U_SQUARED]: 'square units',
  [CONSTANTS.KEY_ID_U_CUBE]: 'cubic units',
};
